import Icon from '@/components/icons/Icon';
import { _t } from '@/locale';
import type { MouseEvent } from 'react';
import { SelectServiceCategory } from '../views/SelectService.types';
import ServiceItem from './ServiceItem';

type Handlers = {
  onPickService: (serviceId: number, isRecommended: boolean) => void;
  onShowServiceInfo?: (serviceId: number) => void;
  /** Passed straight to each row; see ServiceItem's `onOpenServiceInfo`. */
  onOpenServiceInfo?: (serviceId: number) => void;
  /** Passed straight to each row; see ServiceItem's `onHideServiceInfo`. */
  onHideServiceInfo?: (serviceId: number) => void;
  /** Passed straight to each row's CTA; see ServiceItem's `ctaAction`. */
  ctaAction?: 'book' | 'add';
};

type ServiceCategoryListProps = Handlers & {
  categories: SelectServiceCategory[];
  isNarrow: boolean;
  /** When true (active search) every category is forced open regardless of its toggle. */
  forceExpandAll?: boolean;
  /**
   * Per-category expand state, owned by SelectService so it survives a category being
   * filtered out of (and back into) the list during search. A missing id falls back to
   * the category's defaultExpanded.
   */
  expandedById: Record<number, boolean>;
  onToggleCategory: (categoryId: number, next: boolean) => void;
};

const ServiceCategoryList = ({
  categories,
  isNarrow,
  forceExpandAll = false,
  expandedById,
  onToggleCategory,
  ...handlers
}: ServiceCategoryListProps) => (
  <ul className="flex w-full flex-col">
    {categories.map((category, index) => (
      <ServiceCategoryItem
        key={category.id}
        category={category}
        isNarrow={isNarrow}
        withTopBorder={index > 0}
        forceExpanded={forceExpandAll}
        expanded={expandedById[category.id] ?? category.defaultExpanded}
        onToggle={onToggleCategory}
        {...handlers}
      />
    ))}
  </ul>
);

type ServiceCategoryItemProps = Handlers & {
  category: SelectServiceCategory;
  isNarrow: boolean;
  withTopBorder: boolean;
  forceExpanded: boolean;
  expanded: boolean;
  onToggle: (categoryId: number, next: boolean) => void;
};

const ServiceCategoryItem = ({
  category,
  isNarrow,
  withTopBorder,
  forceExpanded,
  expanded,
  onToggle,
  onPickService,
  onShowServiceInfo,
  onOpenServiceInfo,
  onHideServiceInfo,
  ctaAction,
}: ServiceCategoryItemProps) => {
  const isOpen = forceExpanded || expanded;

  const count = category.services.length;
  const countLabel = isNarrow
    ? String(count)
    : count === 1
    ? _t('features.bookingSelection.SelectService.categoryCount_one', { count })
    : _t('features.bookingSelection.SelectService.categoryCount_other', { count });

  const handleToggle = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    if (!forceExpanded) onToggle(category.id, !isOpen);
  };

  return (
    <li className="flex w-full flex-col">
      <div
        onClick={handleToggle}
        className={`py-md gap-sm flex w-full items-center outline-none ${
          withTopBorder ? 'border-border_regular border-t' : ''
        }`}>
        <span className="text-h-s text-fg_primary gap-xs flex flex-1 items-center text-left">
          {category.name}
          {category.icon && <Icon variant={category.icon.variant} color={category.icon.color} size="sm" />}
        </span>
        <span className="text-l text-fg_secondary shrink-0">{countLabel}</span>
        <button
          type="button"
          aria-expanded={isOpen}
          aria-label={_t(`components.modules.Services.ariaLabel.${isOpen ? 'close' : 'open'}`, {
            name: category.name,
          })}
          onClick={handleToggle}
          className="bg-bg_secondary_bold p-xs focus-visible:outline-border_focus flex shrink-0 items-center justify-center rounded-full outline-none focus-visible:outline focus-visible:outline-4">
          <Icon variant={isOpen ? 'chevron-up' : 'chevron-down'} size="sm" color="fg_primary" />
        </button>
      </div>
      {isOpen && (
        <ul className="gap-sm pb-xl flex flex-col">
          {category.services.map((service) => (
            <ServiceItem
              key={service.id}
              service={service}
              onPickService={onPickService}
              onShowServiceInfo={onShowServiceInfo}
              onOpenServiceInfo={onOpenServiceInfo}
              onHideServiceInfo={onHideServiceInfo}
              ctaAction={ctaAction}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

export default ServiceCategoryList;
